import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Button, TextField, Alert, Dialog, DialogTitle, DialogContent, DialogActions, RadioGroup } from '@glplus/glpweb-dsl';
import type { BulkAction } from '../../lib/vendorApiClient';
import { parseMoney, todayDay } from '../../lib/money';
import { MUTED } from './parts';

export type BulkDialogKind = 'put_on_sale' | 'change_price';

/**
 * The two bulk actions that need a number: a sale (percent off or one sale
 * price, with an optional last day) and a price change (one price for all, or
 * a percent up or down). Hide / show / stock run straight from the bar.
 */
export default function BulkDialog({
  kind,
  count,
  locationName,
  busy,
  error,
  onCancel,
  onSubmit,
}: {
  kind: BulkDialogKind;
  count: number;
  locationName: string;
  busy: boolean;
  error: string | null;
  onCancel: () => void;
  onSubmit: (action: BulkAction, sale?: { saleStartsOn: string | null; saleEndsOn: string | null }) => void;
}) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<'percent' | 'amount'>('percent');
  const [percent, setPercent] = useState('');
  const [direction, setDirection] = useState<'down' | 'up'>('down');
  const [amount, setAmount] = useState('');
  const [startsOn, setStartsOn] = useState(todayDay());
  const [endsOn, setEndsOn] = useState('');
  const [problem, setProblem] = useState<string | null>(null);

  const titleId = `bulk-${kind}-title`;
  const isSale = kind === 'put_on_sale';

  const submit = () => {
    setProblem(null);
    if (isSale && startsOn && endsOn && endsOn < startsOn) return setProblem(t('products.errors.saleEndsBeforeStart'));
    const sale = { saleStartsOn: startsOn || null, saleEndsOn: endsOn || null };
    if (mode === 'amount') {
      const money = parseMoney(amount);
      if (!money.ok || money.cents == null) return setProblem(t('products.errors.priceInvalid'));
      return isSale
        ? onSubmit({ type: 'put_on_sale', salePriceCents: money.cents }, sale)
        : onSubmit({ type: 'set_price', priceCents: money.cents });
    }
    const n = Number(percent);
    const max = isSale || direction === 'down' ? 90 : 500;
    if (!/^\d{1,3}(\.\d)?$/.test(percent.trim()) || n < 1 || n > max) {
      return setProblem(t('products.errors.percentInvalid', { max }));
    }
    return isSale
      ? onSubmit({ type: 'put_on_sale', percentOff: n }, sale)
      : onSubmit({ type: 'adjust_price', percent: direction === 'down' ? -n : n });
  };

  return (
    <Dialog open onClose={busy ? undefined : onCancel} maxWidth="sm" fullWidth ariaLabelledBy={titleId} dataTestId={`products-bulk-dialog-${kind}`}>
      <DialogTitle id={titleId}>{t(`products.bulkDialog.${kind}.title`)}</DialogTitle>
      <DialogContent>
        <Typography sx={{ color: MUTED, fontSize: 14, mb: 2 }}>{t('products.bulkDialog.scope', { count, location: locationName })}</Typography>

        <RadioGroup
          ariaLabel={t(`products.bulkDialog.${kind}.how`)}
          name={`bulk-${kind}-how`}
          value={mode}
          onChange={(e) => setMode(e.target.value as 'percent' | 'amount')}
          options={[
            { value: 'percent', label: t(`products.bulkDialog.${kind}.byPercent`) },
            { value: 'amount', label: t(`products.bulkDialog.${kind}.byAmount`) },
          ]}
        />

        <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          {mode === 'percent' ? (
            <>
              {!isSale && (
                <RadioGroup
                  ariaLabel={t('products.bulkDialog.change_price.direction')}
                  name="bulk-change-price-direction"
                  value={direction}
                  onChange={(e) => setDirection(e.target.value as 'down' | 'up')}
                  row
                  options={[
                    { value: 'down', label: t('products.bulkDialog.change_price.lower') },
                    { value: 'up', label: t('products.bulkDialog.change_price.raise') },
                  ]}
                />
              )}
              <Box sx={{ width: 160 }}>
                <TextField
                  ariaLabel={t('products.bulkDialog.percent')}
                  label={t('products.bulkDialog.percent')}
                  value={percent}
                  onChange={(e) => setPercent(e.target.value)}
                  size="small"
                  fullWidth
                  dataTestId="products-bulk-percent"
                />
              </Box>
            </>
          ) : (
            <Box sx={{ width: 180 }}>
              <TextField
                ariaLabel={t(`products.bulkDialog.${kind}.amount`)}
                label={t(`products.bulkDialog.${kind}.amount`)}
                placeholder="0.00"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                size="small"
                fullWidth
                dataTestId="products-bulk-amount"
              />
            </Box>
          )}
          {isSale && (
            <Box sx={{ width: 200 }}>
              <TextField
                ariaLabel={t('products.saleStartsLabel')}
                label={t('products.saleStartsLabel')}
                type="date"
                value={startsOn}
                onChange={(e) => setStartsOn(e.target.value)}
                size="small"
                fullWidth
                helperText={t('products.saleStartsHint')}
                slotProps={{ inputLabel: { shrink: true }, htmlInput: { min: todayDay() } }}
                dataTestId="products-bulk-sale-starts"
              />
            </Box>
          )}
          {isSale && (
            <Box sx={{ width: 260 }}>
              <TextField
                ariaLabel={t('products.saleEnds')}
                label={t('products.saleEnds')}
                type="date"
                value={endsOn}
                onChange={(e) => setEndsOn(e.target.value)}
                size="small"
                fullWidth
                helperText={t('products.saleEndsHint')}
                slotProps={{ inputLabel: { shrink: true }, htmlInput: { min: startsOn || todayDay() } }}
                dataTestId="products-bulk-sale-ends"
              />
            </Box>
          )}
        </Box>

        {(problem || error) && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {problem ?? error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button ariaLabel={t('common.cancel')} buttonText={t('common.cancel')} variant="text" onClick={onCancel} disabled={busy} />
        <Button
          ariaLabel={t(`products.bulkDialog.${kind}.submit`)}
          buttonText={t(`products.bulkDialog.${kind}.submit`)}
          variant="contained"
          color="primary"
          onClick={submit}
          disabled={busy}
          dataTestId="products-bulk-submit"
        />
      </DialogActions>
    </Dialog>
  );
}
